/**
 * IncomingCallModal  (ADDITIVE — INBOUND CALLING, Phase IV)
 *
 * Globally mounted inside the authenticated shell, so it appears over ANY CRM
 * route — Contacts, Settings, Dashboard, anywhere — not just /phone.
 *
 * It renders ONLY from a real `Device.on('incoming')` event surfaced through
 * TelephonyProvider. It cannot be conjured by arbitrary app state: the only way
 * to reach `incoming.kind === 'incoming_ringing'` is for the provider (which is
 * the sole holder of the Twilio Call) to put it there, and the provider only
 * does so when the server granted `incomingAllow` AND this tab is the leader.
 *
 * It never touches a Twilio object. Accept/Reject go through the provider.
 */

import { Phone, PhoneOff, User } from 'lucide-react';
import { useTelephony } from '../../telephony/telephonyContext';
import { useIncomingRingtone } from '../../telephony/useIncomingRingtone';
import { RecordCallToggle } from './RecordCallToggle';

/**
 * Render a phone number readably without pretending to be a full libphonenumber.
 * Falls back to the raw string — an unformattable number is still shown, never
 * hidden, because the user needs to see who is calling.
 */
export function formatCallerPhone(raw: string): string {
  if (!raw) return 'Unknown caller';
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

export function IncomingCallModal() {
  const {
    incoming,
    acceptIncoming,
    rejectIncoming,
    dismissIncoming,
    incomingRecordingPreference,
    setIncomingRecordingPreference,
  } = useTelephony();

  // Audible alert while the call is actively ringing (stops on accept/decline/
  // cancel). Called unconditionally, before any early return, per hook rules.
  useIncomingRingtone(incoming.kind === 'incoming_ringing');

  if (incoming.kind === 'idle') return null;

  // Terminal states get a brief, dismissible notice rather than a ringing modal.
  if (
    incoming.kind === 'rejected' ||
    incoming.kind === 'canceled' ||
    incoming.kind === 'failed'
  ) {
    const label =
      incoming.kind === 'rejected'
        ? 'Call declined'
        : incoming.kind === 'canceled'
          ? 'Missed call'
          : 'Call failed';

    return (
      <div
        className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 rounded-lg bg-gray-900 px-4 py-3 text-white shadow-lg"
        role="status"
        data-testid="incoming-call-terminal"
      >
        <div className="flex items-center gap-3">
          <span className="text-sm">
            {label} — {formatCallerPhone(incoming.info.fromPhone)}
          </span>
          <button
            type="button"
            onClick={dismissIncoming}
            className="text-xs text-gray-300 underline hover:text-white"
            data-testid="incoming-call-dismiss"
          >
            Dismiss
          </button>
        </div>
      </div>
    );
  }

  const { info } = incoming;
  const accepting = incoming.kind === 'accepting';

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="incoming-call-title"
      data-testid="incoming-call-modal"
    >
      <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 animate-pulse items-center justify-center rounded-full bg-green-100">
            <User className="h-8 w-8 text-green-600" aria-hidden="true" />
          </div>

          <p
            id="incoming-call-title"
            className="text-sm font-medium uppercase tracking-wide text-gray-500"
          >
            Incoming call
          </p>

          {info.callerName ? (
            <>
              <h2
                className="mt-1 text-xl font-semibold text-gray-900"
                data-testid="incoming-caller-name"
              >
                {info.callerName}
              </h2>
              <p className="mt-1 text-sm text-gray-600" data-testid="incoming-caller-phone">
                {formatCallerPhone(info.fromPhone)}
              </p>
            </>
          ) : (
            <h2
              className="mt-1 text-xl font-semibold text-gray-900"
              data-testid="incoming-caller-phone"
            >
              {formatCallerPhone(info.fromPhone)}
            </h2>
          )}

          {/*
            ADDITIVE (incoming per-call recording). The old static
            "This call will be recorded" line was misleading — it read
            from a non-authoritative custom parameter that never
            reflected an actual recording state. Removed. The new
            <RecordCallToggle/> below is authoritative for THIS Call
            SID and drives the actual recording start after accept.
          */}
          <RecordCallToggle
            record={incomingRecordingPreference}
            disabled={accepting}
            onChange={(next) => {
              const sid = info.providerCallSid;
              if (!sid) return;
              setIncomingRecordingPreference(sid, next);
            }}
          />

          <div className="mt-6 flex w-full gap-3">
            <button
              type="button"
              onClick={rejectIncoming}
              disabled={accepting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-3 font-medium text-white hover:bg-red-700 disabled:opacity-50"
              data-testid="incoming-call-reject"
            >
              <PhoneOff className="h-5 w-5" aria-hidden="true" />
              Decline
            </button>

            <button
              type="button"
              onClick={() => void acceptIncoming()}
              disabled={accepting}
              className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 font-medium text-white hover:bg-green-700 disabled:opacity-50"
              data-testid="incoming-call-accept"
            >
              <Phone className="h-5 w-5" aria-hidden="true" />
              {accepting ? 'Connecting…' : 'Accept'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default IncomingCallModal;
